import React from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import {
  CalendarMonthOutlined,
  ChevronRightRounded,
  OpenInFullRounded,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEconomicCalendar } from "../../hooks/useEconomicCalendar";
import { useAnalytics } from "../../hooks/useAnalytics";
import { hapticSelection } from "../../lib/haptics";
import EconomicCalendarModal from "./EconomicCalendarModal";
import EconomicEventRow from "./EconomicEventRow";
import CondensedEconomicEventRow from "./CondensedEconomicEventRow";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import {
  buildCalendarGroups,
  buildCondensedGroups,
  eventsForDay,
  CONDENSED_MAX_ROWS,
  PANEL_ROW_GAP,
  PANEL_ROW_HEIGHT,
  PANEL_SLOTS,
} from "./economicCalendarSelection";
import {
  focusRingSx,
  panelBorder,
  panelSurface,
  panelTitleSx,
  riseIn,
  sectionHeaderSx,
} from "./overviewPanel";
import { touchHitAreaSx } from "./EarningsCalendar/touchHitArea";

dayjs.extend(utc);

// The home page's economic calendar — the Sectors page's tape (which stays
// exactly as it is) rehoused as a floating header over open event cards, so
// it stands beside the markets grid as an equal in the page's headerless
// idiom.
//
// Same editorial rules as the tape: US and UK only, high-impact first
// (medium only when nothing high is on, low never), deterministic order so
// rows never reshuffle between polls, and today's impact dots pulse until
// the actual lands.
//
// The desktop band is a RAIL of four fixed slots, so it is the same shape
// every day whatever the week holds. A busy today fills it and hands the
// rest to the door in the last slot; a quiet today pulls the coming days'
// events forward into the free slots, each carrying its date on the card
// (above its time) rather than under a label of its own — the rhythm never
// breaks for a heading. The header's expand button opens the full calendar,
// where every day and every tier is reachable.

const EXPAND_BUTTON_SIZE = 24;

interface OverviewEconomicCalendarProps {
  /** Staggers the panel's entrance behind the markets grid. */
  enterDelayMs?: number;
}

const OverviewEconomicCalendar: React.FC<OverviewEconomicCalendarProps> = ({
  enterDelayMs = 0,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { data, isLoading, error, refetch } = useEconomicCalendar();
  const { trackFeature } = useAnalytics();
  const [modalOpen, setModalOpen] = React.useState(false);
  const isCondensed = useTouchLayout();

  // The server groups events by UTC day, so the cut (and the "Today" chip)
  // must use the SAME clock — a local-midnight today would drop or mislabel
  // the boundary day for any non-UTC viewer. Row times still render local.
  const today = dayjs.utc().format("YYYY-MM-DD");
  const grouped = data?.data.groupedByDate;

  // Phones and the native app take the condensed cut: the lead day only,
  // three rows, everything else behind the door. Pull-forward earns its place
  // in the desktop rail, which has a fixed height to fill beside the markets
  // grid; stacked in a phone column it is just scroll between the user and
  // their watchlist. The selection arithmetic lives in
  // economicCalendarSelection so both cuts stay testable.
  const groups = React.useMemo(
    () =>
      isCondensed
        ? buildCondensedGroups(grouped, today)
        : buildCalendarGroups(grouped, today),
    [grouped, today, isCondensed],
  );

  const leadDate = groups[0]?.date ?? today;
  const isToday = leadDate === today;
  const dayLabel = dayjs.utc(leadDate).format("ddd D MMM");

  // The door stays TIER-PURE: "+N more" counts only the display tier's
  // overflow, so one high event never advertises the mediums beneath it.
  // The calendar it opens shows the WHOLE day, every tier reachable —
  // "View all" means all (Ben, 2026-08-28).
  const leadTierEvents = React.useMemo(
    () => eventsForDay(grouped?.[leadDate]),
    [grouped, leadDate],
  );
  const shownLeadCount = groups[0]?.events.length ?? 0;
  const hiddenLeadCount = Math.max(leadTierEvents.length - shownLeadCount, 0);
  const doorDay = isToday ? "today" : "this day";
  const doorLabel = `${hiddenLeadCount} more ${doorDay}`;

  // A failed first load says so, with a way back; a poll that fails after a
  // successful one keeps the last rail rather than blanking it. Only a
  // genuinely quiet stretch reads as "no events" — an outage never does.
  const failed = !isLoading && error != null && data === undefined;
  const quiet = !isLoading && !failed && groups.length === 0;
  // The header's red mark means "a high-impact print is on the rail"; it is
  // information, not decoration, so a medium-only or empty rail goes without.
  const hasHighImpact = groups.some((group) =>
    group.events.some((event) => event.impact === "High"),
  );

  // The quiet card the rail falls back to when it has no rows to show.
  const quietCardSx = {
    flex: 1,
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    px: 2,
    py: 3,
    borderRadius: 1.5,
    backgroundColor: panelSurface(isDark),
    border: `1px solid ${panelBorder(isDark)}`,
  } as const;

  const handleOpenModal = (source: "door" | "header") => {
    hapticSelection();
    setModalOpen(true);
    trackFeature("econ_calendar_modal_opened", {
      hidden: hiddenLeadCount,
      source,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        ...riseIn(enterDelayMs),
      }}
    >
      {/* Floating header, on the same optical line as the markets' */}
      <Box sx={sectionHeaderSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.875,
            minWidth: 0,
          }}
        >
          <CalendarMonthOutlined
            sx={{ fontSize: 14, color: "#22c55e", flexShrink: 0 }}
          />
          {/* Still marker, only while a high-impact print is on the rail —
              the pending ROWS carry the pulse, so the header stays quiet */}
          {hasHighImpact && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "#ef4444",
                flexShrink: 0,
              }}
            />
          )}
          <Typography
            sx={{
              ...panelTitleSx,
              color: theme.palette.text.secondary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Economic Calendar
          </Typography>
        </Box>
        {/* The way into the full calendar, which opens on the rail's lead
            day. The rail's unlabelled rows are today's; when the lead is a
            later day, the rail says which. */}
        <Box
          sx={{
            ml: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {!isToday && (
            <Typography
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: theme.palette.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              {dayLabel}
            </Typography>
          )}
          <Box
            component="button"
            onClick={() => handleOpenModal("header")}
            aria-label="Open the full economic calendar"
            sx={{
              all: "unset",
              ...focusRingSx,
              // 24px to look at, 44px tall to hit on touch
              ...(isCondensed && touchHitAreaSx(EXPAND_BUTTON_SIZE)),
              boxSizing: "border-box",
              cursor: "pointer",
              width: EXPAND_BUTTON_SIZE,
              height: EXPAND_BUTTON_SIZE,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0.75,
              color: theme.palette.text.secondary,
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.05)"
                : "rgba(0, 0, 0, 0.05)",
              WebkitTapHighlightColor: "transparent",
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": {
                color: "#22c55e",
                backgroundColor: "rgba(34, 197, 94, 0.14)",
              },
              "&:active": { transform: "scale(0.94)" },
            }}
          >
            <OpenInFullRounded sx={{ fontSize: 13 }} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // Condensed rows carry their own hairlines and sit flush; the
          // desktop cards need air between them.
          gap: isCondensed ? 0 : `${PANEL_ROW_GAP}px`,
        }}
      >
        {isLoading &&
          Array.from(
            { length: isCondensed ? CONDENSED_MAX_ROWS : PANEL_SLOTS },
            (_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                animation="wave"
                height={isCondensed ? 40 : PANEL_ROW_HEIGHT}
                sx={{
                  flex: "0 0 auto",
                  my: isCondensed ? 0.5 : 0,
                  borderRadius: isCondensed ? 0.75 : 1.5,
                  bgcolor: isDark
                    ? "rgba(255,255,255,0.09)"
                    : "rgba(0,0,0,0.09)",
                }}
              />
            ),
          )}

        {/* Phones: the lead day only, on hairlines. */}
        {!isLoading &&
          isCondensed &&
          groups.map((group) => (
            <React.Fragment key={group.date}>
              {group.events.map((event, index) => (
                <CondensedEconomicEventRow
                  key={`${event.date}-${event.country}-${event.event}-${index}`}
                  event={event}
                  pulse={group.date === today}
                  // The door owns the last hairline when there is one, so
                  // the list never ends on a rule with nothing under it.
                  divider={
                    index < group.events.length - 1 || hiddenLeadCount > 0
                  }
                />
              ))}
            </React.Fragment>
          ))}

        {/* The condensed door. No ghost card — a borderless action line
              indented to the event names' own left edge, so it reads as the
              list's last entry rather than a button dropped under it. The
              count stays TIER-PURE like the desktop door: it is the display
              tier's overflow, never a promise about the whole day. */}
        {!isLoading && isCondensed && hiddenLeadCount > 0 && (
          <Box
            component="button"
            onClick={() => handleOpenModal("door")}
            sx={{
              all: "unset",
              ...focusRingSx,
              boxSizing: "border-box",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: "100%",
              pl: "16px",
              py: 1.125,
              fontFamily: "inherit",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "#22c55e",
              WebkitTapHighlightColor: "transparent",
              transition: "opacity 0.2s ease",
              "&:active": { opacity: 0.6 },
            }}
          >
            {doorLabel}
            <ChevronRightRounded
              sx={{ fontSize: 16, flexShrink: 0, ml: -0.25 }}
            />
          </Box>
        )}

        {/* Desktop: the rail. Pulled-forward rows carry their date on the
              card; the lead day is named by the header's chip. */}
        {!isLoading &&
          !isCondensed &&
          groups.map((group, groupIndex) => (
            <React.Fragment key={group.date}>
              {group.events.map((event, index) => (
                <EconomicEventRow
                  key={`${event.date}-${event.country}-${event.event}-${index}`}
                  event={event}
                  pulse={group.date === today}
                  dateLabel={
                    groupIndex > 0
                      ? dayjs.utc(group.date).format("ddd D")
                      : undefined
                  }
                />
              ))}
            </React.Fragment>
          ))}

        {/* The door takes the rail's last slot — a full-size ghost card,
              hairline only, saying exactly how much of the day the panel is
              holding back, with the way in at the far edge. Opens the full
              calendar on this day. */}
        {!isLoading && !isCondensed && hiddenLeadCount > 0 && (
          <Box
            component="button"
            onClick={() => handleOpenModal("door")}
            sx={{
              all: "unset",
              ...focusRingSx,
              boxSizing: "border-box",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 2,
              height: PANEL_ROW_HEIGHT,
              borderRadius: 1.5,
              border: `1px dashed ${panelBorder(isDark)}`,
              fontFamily: "inherit",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              transition:
                "color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
              WebkitTapHighlightColor: "transparent",
              "&:hover": {
                color: "#22c55e",
                borderColor: "rgba(34, 197, 94, 0.45)",
                backgroundColor: isDark
                  ? "rgba(34, 197, 94, 0.05)"
                  : "rgba(34, 197, 94, 0.04)",
              },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <span>
              +{hiddenLeadCount} more {doorDay}
            </span>
            <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
              View all
              <ChevronRightRounded sx={{ fontSize: 18, ml: -0.25 }} />
            </Box>
          </Box>
        )}

        {failed && (
          <Box sx={quietCardSx}>
            <CalendarMonthOutlined
              sx={{ fontSize: 22, color: theme.palette.text.disabled }}
            />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
                textAlign: "center",
              }}
            >
              Couldn&apos;t load the calendar
            </Typography>
            <Box
              component="button"
              onClick={() => refetch()}
              sx={{
                all: "unset",
                ...focusRingSx,
                boxSizing: "border-box",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#22c55e",
                minHeight: 44,
                px: 2,
                WebkitTapHighlightColor: "transparent",
                transition: "opacity 0.2s ease",
                "&:hover": { opacity: 0.8 },
                "&:active": { opacity: 0.6 },
              }}
            >
              Retry
            </Box>
          </Box>
        )}

        {quiet && (
          <Box sx={quietCardSx}>
            <CalendarMonthOutlined
              sx={{ fontSize: 22, color: theme.palette.text.disabled }}
            />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
                textAlign: "center",
              }}
            >
              No major events scheduled
            </Typography>
          </Box>
        )}
      </Box>

      <EconomicCalendarModal
        open={modalOpen}
        grouped={grouped}
        dateRange={data?.data.dateRange}
        initialDate={leadDate}
        today={today}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default OverviewEconomicCalendar;
